import React, {useReducer, forwardRef, useImperativeHandle} from 'react'
import aSync from 'async'

import {Modal, ModalHeader, ModalActions, S2, Button, P, Alert} from '../UI/index.js'
import {formSpacing, Check, FormRow, FormStack, TimePicker} from '../UI/forms/index.js'
import reducer from '../../helpers/reducer.js'
import t from '../../helpers/Languages.js'

import OrderActions from '../../actions/OrderActions.js'

const initialState = {
    isOpen: false,
    ids: [],
    startTimeEnabled: false,
    endTimeEnabled: false,
    startTime: '',
    endTime: '',
    loading: false,
    errors: []
}

/**
 * Without props it changes the timeslot of the selected orders. text, countText and onConfirm make it work on other
 * items: onConfirm({startTimeEnabled, startTime, endTimeEnabled, endTime}, ids) is called after the popup closes.
 * requireField disables Bevestigen while no field is checked.
 */
const ChangeTimeslotPopup = forwardRef(({text, countText, onConfirm: onConfirmProp, requireField}, ref) => {
    const [{isOpen, ids, startTimeEnabled, endTimeEnabled, startTime, endTime, errors, loading}, dispatch] = useReducer(reducer, initialState)

    const open = (ids) => {
        dispatch({isOpen: true, ids})
    }

    const close = () => {
        dispatch(initialState)
    }

    const onConfirm = async () => {
        if (onConfirmProp) {
            close()
            onConfirmProp({startTimeEnabled, startTime, endTimeEnabled, endTime}, ids)
            return
        }

        OrderActions.setSelected([])

        dispatch({loading: true})

        const errors = []

        await aSync.eachSeries(ids, (id, next) => {
            OrderActions.changeTimeslot(id, startTimeEnabled && startTime, endTimeEnabled && endTime, (err) => {
                if (err) {
                    errors.push(err)
                }

                next()
            })
        })

        if (errors.length) {
            dispatch({errors, loading: false})
        } else {
            close()
        }
    }

    // To make passed functions accessible to parents
    useImperativeHandle(ref, () => ({open, close}))

    return (
        <Modal style={{maxWidth: 400}} show={isOpen} onClose={close}>
            <ModalHeader title={t('timeslotChange')} />

            <P style={{marginBottom: 10}}>{text || t('changeTimeslotOrdersText')}</P>

            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <FormRow gap={formSpacing.tight}>
                    <Check onChange={(event) => dispatch({startTimeEnabled: event.target.checked})} checked={startTimeEnabled} />
                    <TimePicker
                        label={t('startTime')}
                        value={startTime}
                        onChange={(event) => dispatch({startTime: event.target.value})}
                        disabled={!startTimeEnabled}
                    />
                </FormRow>
                <FormRow gap={formSpacing.tight}>
                    <Check onChange={(event) => dispatch({endTimeEnabled: event.target.checked})} checked={endTimeEnabled} />
                    <TimePicker
                        label={t('endTime')}
                        value={endTime}
                        onChange={(event) => dispatch({endTime: event.target.value})}
                        disabled={!endTimeEnabled}
                    />
                </FormRow>
            </FormStack>

            {errors.length > 0 && errors.map((error, index) => {
                return <Alert style={{marginBottom: 6}} key={`error${index}`} variant='danger'>{error}</Alert>
            })}

            <ModalActions
                start={ids.length > 1 &&
                    <>
                        <i style={{fontSize: 12, marginRight: 3}} className='mdi mdi-check' />
                        <S2>{countText ? countText(ids.length) : t('ordersSelectedCount', {count: ids.length})}</S2>
                    </>
                }
            >
                {!errors.length &&
                    <Button
                        variant='text'
                        onClick={close}
                    >
                        {t('cancel')}
                    </Button>
                }

                {!errors.length &&
                    <Button
                        variant='text'
                        loading={loading}
                        disabled={requireField && !startTimeEnabled && !endTimeEnabled}
                        onClick={onConfirm}
                    >
                        {t('confirm')}
                    </Button>
                }

                {!!errors.length &&
                    <Button
                        variant='text'
                        onClick={close}
                    >
                        {t('close')}
                    </Button>
                }
            </ModalActions>
        </Modal>
    )
})
ChangeTimeslotPopup.displayName = 'ChangeTimeslotPopup'

export default ChangeTimeslotPopup
